<template>
  <div class="rv-page">
    <div class="rv-scope">
      <v-select
        v-model="partnerId"
        :items="partners"
        item-text="name"
        item-value="id"
        label="Partner"
        outlined
        dense
        hide-details
        :disabled="loading"
        class="rv-scope__field rv-scope__field--wide"
        @change="onPartnerChange"
      />

      <v-select
        v-model="programBatchId"
        :items="batchItems"
        item-text="label"
        item-value="id"
        label="Batch"
        outlined
        dense
        hide-details
        :disabled="loading || !partnerId"
        class="rv-scope__field"
      />

      <v-btn
        depressed
        color="primary"
        :disabled="!partnerId || loading"
        :loading="loading"
        @click="runSearch"
      >
        <v-icon left>mdi-magnify</v-icon>
        Search
      </v-btn>

      <v-chip v-if="partner" x-small label outlined class="rv-scope__chip">
        {{ partner.type }}
      </v-chip>

      <!--
     <v-chip
        v-if="profile"
        x-small
        label
        :color="profile.is_saved ? 'success' : 'warning'"
        text-color="white"
        class="rv-scope__chip"
      >
        {{ profile.is_saved ? scopeLabel : "Billing profile not set" }}
      </v-chip>-->

      <v-spacer />

      <span v-if="isDirty" class="rv-scope__dirty">
        <v-icon x-small color="warning">mdi-circle-medium</v-icon>
        Unsaved changes
      </span>
      <v-btn
        depressed
        color="primary"
        class="ml-2"
        :disabled="!isDirty"
        :loading="saving"
        @click="save"
      >
        Save
      </v-btn>
      <v-btn v-if="partnerId" color="primary" class="ml-2" @click="goToSummary">
        <v-icon small left>mdi-chart-box-outline</v-icon>
        Open Summary
      </v-btn>
    </div>

    <div v-if="loading" class="rv-state">
      <v-progress-circular indeterminate color="primary" size="40" width="3" />
      <p class="rv-state__text">Loading partner revenue configurations...</p>
    </div>

    <v-card v-else-if="!loaded" outlined class="rv-state rv-state--card">
      <h6 class="rv-state__title">
        Choose a partner to configure partner revenue share ...
      </h6>
    </v-card>

    <v-card
      v-else-if="!programmes.length || !feeTypes.length"
      outlined
      class="rv-state rv-state--card"
    >
      <v-icon size="44" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
      <h3 class="rv-state__title">Nothing to configure</h3>
      <p class="rv-state__text">
        This partner has no programmes mapped, or no fee types are available for
        it.
      </p>
    </v-card>

    <template v-else>
      <div class="rv-cards">
        <v-card
          v-for="entry in categoryRates"
          :key="entry.category_key"
          outlined
          class="rv-card"
          :class="{ 'rv-card--unset': !entry.is_set }"
        >
          <div class="rv-card__head">
            <h3 class="rv-card__name">{{ entry.category_name }}</h3>
            <v-chip x-small label outlined class="rv-card__hsn">
              HSN {{ entry.hsn }}
            </v-chip>
          </div>

          <p class="rv-card__charged">Charged on {{ entry.charged_on }}</p>

          <div class="rv-card__inputs">
            <v-text-field
              v-model.number="entry.rate"
              label="Share"
              suffix="%"
              type="number"
              min="0"
              max="100"
              outlined
              dense
              hide-details
              class="rv-card__input"
            />
            <v-text-field
              v-model.number="entry.gst_percentage"
              label="GST"
              suffix="%"
              type="number"
              min="0"
              max="100"
              outlined
              dense
              hide-details
              class="rv-card__input"
              @input="onGstInput(entry)"
            />
          </div>

          <v-btn-toggle
            v-if="Number(entry.gst_percentage) > 0"
            v-model="entry.gst_type"
            mandatory
            dense
            class="rv-card__toggle"
          >
            <v-btn x-small value="Inclusive">Inclusive</v-btn>
            <v-btn x-small value="Exclusive">Exclusive</v-btn>
          </v-btn-toggle>
          <p v-else class="rv-card__nogst">No GST on this category</p>

          <p class="rv-card__effect">{{ effectLabel(entry) }}</p>

          <p v-if="!entry.is_set" class="rv-card__unsetnote">
            Not saved — currently falling back to this default
          </p>
        </v-card>
      </div>

      <v-card outlined class="rv-table-card">
        <div class="rv-table-card__head">
          <div>
            <h3 class="rv-table-card__title">Fee share</h3>
            <p class="rv-table-card__sub">
              Each fee type starts on its category rate above. Change one here
              only when a fee type settles differently from the rest of its
              category.
            </p>
          </div>

          <div class="rv-table-card__tools">
            <v-select
              v-model="categoryFilter"
              :items="categoryFilterItems"
              item-text="text"
              item-value="value"
              label="Category"
              outlined
              dense
              hide-details
              class="rv-tool"
            />
            <v-select
              v-model="programmeFilter"
              :items="programmeFilterItems"
              item-text="text"
              item-value="value"
              label="Programme"
              outlined
              dense
              hide-details
              class="rv-tool"
            />
          </div>
        </div>

        <v-card v-if="!visibleProgrammes.length" flat class="rv-state">
          <v-icon size="36" color="grey lighten-1">mdi-magnify-close</v-icon>
          <p class="rv-state__text mt-2">Nothing matches these filters.</p>
        </v-card>

        <v-expansion-panels
          v-else
          v-model="openPanels"
          multiple
          accordion
          flat
          class="rv-panels"
        >
          <v-expansion-panel
            v-for="group in visibleProgrammes"
            :key="group.program_id"
            class="rv-panel"
          >
            <v-expansion-panel-header class="rv-panel__header">
              <div class="rv-panel__title">
                <span class="rv-panel__name">{{ group.label }}</span>
                <span class="rv-panel__meta">
                  {{ group.configured }}/{{ group.rows.length }} set
                </span>
              </div>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="rv-panel__content">
              <table class="rv-table">
                <thead>
                  <tr>
                    <th class="rv-th">Fee Type</th>
                    <th class="rv-th rv-th--num">Share %</th>
                    <th class="rv-th rv-th--num">GST %</th>
                    <th class="rv-th">GST Type</th>
                  </tr>
                </thead>
                <tbody
                  v-for="section in group.sections"
                  :key="`c-${group.program_id}-${section.category}`"
                >
                  <tr>
                    <td class="rv-band" colspan="4">
                      <span class="rv-band__name">{{ section.category }}</span>
                      <v-btn
                        x-small
                        text
                        color="primary"
                        class="rv-band__copy"
                        @click="applySectionRate(group, section)"
                      >
                        apply category rate
                      </v-btn>
                    </td>
                  </tr>
                  <tr
                    v-for="row in section.rows"
                    :key="`${group.program_id}-${row.fee_code}`"
                  >
                    <td class="rv-td">
                      <span class="rv-fee">
                        <span class="rv-fee__code">{{ row.fee_code }}</span>
                        <span class="rv-fee__name">{{ row.fee_name }}</span>
                        <span v-if="row.inherited" class="rv-fee__inherited">
                          follows category
                        </span>
                      </span>
                    </td>
                    <td class="rv-td rv-td--num">
                      <v-text-field
                        v-model.number="row.fee_percentage"
                        suffix="%"
                        type="number"
                        min="0"
                        max="100"
                        dense
                        outlined
                        hide-details
                        :disabled="saving"
                        class="rv-input"
                        @input="onRowEdit(row)"
                      />
                    </td>
                    <td class="rv-td rv-td--num">
                      <v-text-field
                        v-model.number="row.gst_percentage"
                        suffix="%"
                        type="number"
                        min="0"
                        max="100"
                        dense
                        outlined
                        hide-details
                        :disabled="saving || !Number(row.fee_percentage)"
                        class="rv-input"
                        @input="onRowEdit(row)"
                      />
                    </td>
                    <td class="rv-td">
                      <v-btn-toggle
                        v-if="Number(row.gst_percentage) > 0"
                        v-model="row.gst_type"
                        mandatory
                        dense
                        class="rv-toggle"
                      >
                        <v-btn x-small value="Inclusive">Inclusive</v-btn>
                        <v-btn x-small value="Exclusive">Exclusive</v-btn>
                      </v-btn-toggle>
                      <span v-else class="rv-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card>
    </template>

    <v-dialog v-model="bulkDialog" max-width="440px">
      <v-card v-if="bulkCategory">
        <v-card-title class="font-weight-medium secondary--text primary py-3">
          <span>{{ bulkCategory.name }}</span>
          <v-spacer />
          <v-btn icon x-small @click="bulkDialog = false">
            <v-icon x-small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="rv-bulk__hint">
            Applies to every fee type in this category, across
            {{ visibleProgrammes.length }}
            {{ visibleProgrammes.length === 1 ? "programme" : "programmes" }}
            currently shown.
          </p>
          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model.number="bulk.rate"
                label="Share %"
                type="number"
                min="0"
                max="100"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model.number="bulk.gst"
                label="GST %"
                type="number"
                min="0"
                max="100"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col v-if="Number(bulk.gst) > 0" cols="12">
              <v-btn-toggle v-model="bulk.gstType" mandatory dense class="mt-2">
                <v-btn small value="Inclusive">Inclusive</v-btn>
                <v-btn small value="Exclusive">Exclusive</v-btn>
              </v-btn-toggle>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text small @click="bulkDialog = false">Cancel</v-btn>
          <v-btn small depressed color="primary" @click="applyBulk"
            >Apply</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "RevenueRateSetup",

  data() {
    return {
      partners: [],
      programmes: [],
      programBatches: [],
      feeTypes: [],
      categories: [],
      categoryRates: [],
      rows: [],
      profile: null,
      partnerId: null,
      programBatchId: null,
      categoryFilter: null,
      programmeFilter: null,
      openPanels: [],
      loading: false,
      saving: false,

      loaded: false,
      snapshot: "[]",
      rateSnapshot: "[]",
      bulkDialog: false,
      bulkCategory: null,
      bulk: { rate: 0, gst: 0, gstType: "Exclusive" },
      requestId: 0,
    };
  },

  computed: {
    partner() {
      return this.partners.find((item) => item.id === this.partnerId);
    },

    scopeLabel() {
      return this.profile?.revenue_scope === "all_collections"
        ? "All collections"
        : "Sourced students";
    },

    batchItems() {
      const programIds = this.programmes.map((programme) => programme.id);
      const batches = this.programBatches
        .filter((batch) => programIds.includes(batch.program_id))
        .map((batch) => ({
          id: batch.id,
          label: `${batch.batch === 2 ? "Jul" : "Jan"} ${String(
            batch.year,
          ).slice(-2)} · ${this.programmeName(batch.program_id)}`,
        }));

      return [{ id: null, label: "All batches (partner default)" }, ...batches];
    },

    categoryNames() {
      return this.categoryRates.map((entry) => entry.category_name);
    },

    categoryFilterItems() {
      return [
        { text: "All categories", value: null },
        ...this.categoryNames.map((name) => ({ text: name, value: name })),
      ];
    },

    programmeFilterItems() {
      return [
        { text: "All programmes", value: null },
        ...this.programmes.map((programme) => ({
          text: this.programmeName(programme.id),
          value: programme.id,
        })),
      ];
    },

    visibleProgrammes() {
      const order = this.categoryNames;
      const groups = new Map();

      this.rows.forEach((row) => {
        if (this.categoryFilter && row.category !== this.categoryFilter) return;
        if (this.programmeFilter && row.program_id !== this.programmeFilter)
          return;

        if (!groups.has(row.program_id)) {
          groups.set(row.program_id, {
            program_id: row.program_id,
            label: this.programmeName(row.program_id),
            rows: [],
          });
        }
        groups.get(row.program_id).rows.push(row);
      });

      return [...groups.values()].map((group) => {
        const sections = order
          .map((category) => ({
            category,
            rows: group.rows.filter((row) => row.category === category),
          }))
          .filter((section) => section.rows.length);

        return {
          ...group,
          sections,
          configured: group.rows.filter((row) => Number(row.fee_percentage) > 0)
            .length,
        };
      });
    },

    isDirty() {
      return (
        JSON.stringify(this.rows) !== this.snapshot ||
        JSON.stringify(this.categoryRates) !== this.rateSnapshot
      );
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Revenue Split" }]);
    this.initialise();
    this.runSearch();
  },

  methods: {
    async initialise() {
      await Promise.all([
        this.getPartners(),
        this.getFeeTypes(),
        this.getBatches(),
      ]);
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("partners");
        this.partners = (res.data.data || []).filter(
          (partner) => partner.type !== "Sponsor",
        );
      } catch (err) {
        this.notifyError(err, "Unable to load partners");
      }
    },

    async getFeeTypes() {
      try {
        const res = await this.$axios.get("fee_types");
        this.feeTypes = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load fee types");
      }
    },

    async getBatches() {
      try {
        const res = await this.$axios.get("/program_batches?active=true");
        this.programBatches = res.data.data || [];
      } catch (err) {
        this.notifyError(err, "Unable to load batches");
      }
    },

    async onPartnerChange() {
      this.programBatchId = null;
      this.categoryFilter = null;
      this.programmeFilter = null;
      this.loaded = false;
      this.rows = [];
      this.categoryRates = [];
      this.snapshot = "[]";
      this.rateSnapshot = "[]";
      await Promise.all([this.getProgrammes(), this.loadProfile()]);
    },

    async runSearch() {
      if (!this.partnerId) return;
      await this.reload();
      this.loaded = true;
    },

    async getProgrammes() {
      if (!this.partnerId) return;
      try {
        const url =
          this.partner?.type === "Self" || this.partner?.type === "Institute"
            ? "/programs?active=true"
            : `/get_partner_programs/${this.partnerId}`;
        const res = await this.$axios.get(url);
        this.programmes = res.data.data || [];
      } catch (err) {
        this.programmes = [];
        this.notifyError(err, "Unable to load programmes");
      }
    },

    async loadProfile() {
      if (!this.partnerId) return;
      try {
        const res = await this.$axios.get("/revenue_billing_profile", {
          params: { partner_id: this.partnerId },
        });
        this.profile = res.data.data;
      } catch (err) {
        this.profile = null;
      }
    },

    async reload() {
      await Promise.all([this.loadCategoryRates(), this.loadConfig()]);
    },

    async loadCategoryRates() {
      if (!this.partnerId) return;
      try {
        const params = { partner_id: this.partnerId };
        if (this.programBatchId) params.program_batch_id = this.programBatchId;

        const res = await this.$axios.get("/revenue_category_rates", {
          params,
        });
        this.categoryRates = (res.data.data || []).map((entry) => ({
          ...entry,
          rate: Number(entry.rate),
          gst_percentage: Number(entry.gst_percentage),
        }));
        this.rateSnapshot = JSON.stringify(this.categoryRates);
      } catch (err) {
        this.notifyError(err, "Unable to load the category rates");
      }
    },

    onGstInput(entry) {
      if (!Number(entry.gst_percentage)) entry.gst_type = "Exclusive";
      else if (!entry.gst_type) entry.gst_type = "Inclusive";
      this.syncInheritedRows();
    },

    effectLabel(entry) {
      const rate = Number(entry.rate) || 0;
      const gst = Number(entry.gst_percentage) || 0;

      if (!rate) return "Nothing is charged for this category";
      if (!gst) return `${rate}% of the base, no GST — net equals gross`;

      if (entry.gst_type === "Inclusive") {
        return `${rate}% of the base includes GST — net is that ÷ ${(
          1 +
          gst / 100
        ).toFixed(2)}`;
      }
      return `${rate}% of the base is net — ${gst}% GST is added on top`;
    },

    async loadConfig() {
      if (!this.partnerId) return;

      this.loading = true;
      this.requestId += 1;
      const requestId = this.requestId;

      try {
        const params = { partner_id: this.partnerId };
        if (this.programBatchId) params.program_batch_id = this.programBatchId;

        const res = await this.$axios.get("/revenue_split_config", { params });
        if (this.requestId !== requestId) return;

        this.categories = res.data.categories || [];
        const saved = res.data.data || [];

        this.rows = [];
        this.scopedProgrammes().forEach((programme) => {
          this.feeTypes.forEach((fee) => {
            this.feeCategories(fee).forEach((category) => {
              const existing = saved.find(
                (row) =>
                  row.program_id === programme.id &&
                  row.fee_code === fee.code &&
                  row.category === category,
              );

              const defaults = this.categoryDefaults(category);

              this.rows.push({
                id: existing?.id,
                program_id: programme.id,
                program_batch_id: this.programBatchId || null,
                fee_code: fee.code,
                fee_name: fee.name || fee.code,
                category,

                inherited: !existing,
                fee_percentage: existing
                  ? Number(existing.fee_percentage)
                  : defaults.rate,
                gst_percentage: existing
                  ? Number(existing.gst_percentage)
                  : defaults.gst,
                gst_type: existing
                  ? this.resolveGstType(existing)
                  : defaults.gstType,
              });
            });
          });
        });

        this.snapshot = JSON.stringify(this.rows);
        this.openPanels = this.visibleProgrammes.length ? [0] : [];
      } catch (err) {
        this.notifyError(err, "Unable to load the rate configuration");
      } finally {
        if (this.requestId === requestId) this.loading = false;
      }
    },

    scopedProgrammes() {
      if (!this.programBatchId) return this.programmes;
      const batch = this.programBatches.find(
        (item) => item.id === this.programBatchId,
      );
      if (!batch) return this.programmes;
      return this.programmes.filter(
        (programme) => programme.id === batch.program_id,
      );
    },

    feeCategories(fee) {
      const list = fee.categories?.length
        ? fee.categories
        : fee.category
        ? [fee.category]
        : [];
      return list.filter((name) => this.categoryNames.includes(name));
    },

    categoryDefaults(categoryName) {
      const entry = this.categoryRates.find(
        (item) => item.category_name === categoryName,
      );

      return {
        rate: Number(entry?.rate) || 0,
        gst: Number(entry?.gst_percentage) || 0,
        gstType: entry?.gst_type || "Exclusive",
      };
    },

    onRowEdit(row) {
      row.inherited = false;
      if (!Number(row.fee_percentage)) {
        row.gst_percentage = 0;
        row.gst_type = "Exclusive";
      }
    },

    syncInheritedRows() {
      this.rows.forEach((row) => {
        if (!row.inherited) return;
        const defaults = this.categoryDefaults(row.category);
        row.fee_percentage = defaults.rate;
        row.gst_percentage = defaults.gst;
        row.gst_type = defaults.gstType;
      });
    },

    resolveGstType(existing) {
      const type = existing?.gst_type;
      if (["Inclusive", "Exclusive"].includes(type)) return type;
      return "Exclusive";
    },

    programmeName(programId) {
      const programme = this.programmes.find((item) => item.id === programId);
      if (!programme) return `Programme #${programId}`;
      const name = programme.degree?.short_name || programme.name || "";
      return `${name}${
        programme.specialization ? ` - ${programme.specialization}` : ""
      }`;
    },

    categoryRate(category) {
      const rows = this.rows.filter(
        (row) =>
          row.category === category.name && Number(row.fee_percentage) > 0,
      );

      if (!rows.length) {
        return {
          configured: false,
          rate: category.default_rate,
          gst: category.default_gst_percentage,
          gstType: category.default_gst_type,
          count: 0,
        };
      }

      return {
        configured: true,
        rate: this.mode(rows.map((row) => Number(row.fee_percentage))),
        gst: this.mode(rows.map((row) => Number(row.gst_percentage))),
        gstType: this.mode(rows.map((row) => row.gst_type)),
        count: rows.length,
      };
    },

    mode(values) {
      const counts = new Map();
      values.forEach((value) =>
        counts.set(value, (counts.get(value) || 0) + 1),
      );
      return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
    },

    openBulk(category) {
      const current = this.categoryRate(category);
      this.bulkCategory = category;
      this.bulk = {
        rate: Number(current.rate),
        gst: Number(current.gst),
        gstType: current.gstType || "Exclusive",
      };
      this.bulkDialog = true;
    },

    applyBulk() {
      const visibleIds = new Set(
        this.visibleProgrammes.map((group) => group.program_id),
      );

      this.rows.forEach((row) => {
        if (row.category !== this.bulkCategory.name) return;
        if (!visibleIds.has(row.program_id)) return;
        row.fee_percentage = Number(this.bulk.rate) || 0;
        row.gst_percentage = row.fee_percentage
          ? Number(this.bulk.gst) || 0
          : 0;
        row.gst_type = row.gst_percentage ? this.bulk.gstType : "Exclusive";
        row.inherited = false;
      });

      this.bulkDialog = false;
      this.$store.commit("showSnackbar", {
        message: `${this.bulkCategory.name} set to ${this.bulk.rate}%`,
        color: "success",
      });
    },

    applySectionRate(group, section) {
      const category = this.categories.find(
        (item) => item.name === section.category,
      );
      if (!category) return;

      const rate = this.categoryRate(category);
      section.rows.forEach((row) => {
        row.fee_percentage = Number(rate.rate) || 0;
        row.gst_percentage = row.fee_percentage ? Number(rate.gst) || 0 : 0;
        row.gst_type = row.gst_percentage ? rate.gstType : "Exclusive";
        row.inherited = false;
      });

      this.$store.commit("showSnackbar", {
        message: `${section.category} applied to ${group.label}`,
        color: "success",
      });
    },

    async save() {
      this.saving = true;
      try {
        await this.$axios.put("/revenue_category_rates", {
          partner_id: this.partnerId,
          program_batch_id: this.programBatchId || null,
          rates: this.categoryRates.map((entry) => ({
            category_key: entry.category_key,
            rate: Number(entry.rate) || 0,
            gst_percentage: Number(entry.gst_percentage) || 0,
            gst_type: Number(entry.gst_percentage)
              ? entry.gst_type
              : "Exclusive",
          })),
        });

        await this.$axios.put("/revenue_split_config", {
          partner_id: this.partnerId,
          program_batch_id: this.programBatchId || null,
          program_splits: this.rows.map((row) => {
            const fee_percentage = Number(row.fee_percentage) || 0;
            const gst_percentage = fee_percentage
              ? Number(row.gst_percentage) || 0
              : 0;

            const payload = {
              program_id: row.program_id,
              program_batch_id: row.program_batch_id,
              partner_id: this.partnerId,
              fee_code: row.fee_code,
              category: row.category,
              fee_percentage,
              gst_percentage,
              is_gst: gst_percentage > 0,
              gst_type: gst_percentage > 0 ? row.gst_type : null,
            };

            if (row.id) payload.id = row.id;
            return payload;
          }),
        });

        this.$store.commit("showSnackbar", {
          message: "Rates saved",
          color: "success",
        });

        await this.reload();
      } catch (err) {
        this.notifyError(err, "Unable to save the rates");
      } finally {
        this.saving = false;
      }
    },

    goToSummary() {
      this.$router.push({
        name: "Revenue Summary",
        query: { partner_id: this.partnerId },
      });
    },

    formatRate(value) {
      return Number(value || 0).toLocaleString("en-IN", {
        maximumFractionDigits: 2,
      });
    },

    notifyError(err, fallback) {
      const data = err?.response?.data;
      const changeset = data?.errors
        ? Object.entries(data.errors)
            .map(
              ([field, messages]) =>
                `${field} ${[].concat(messages).join(", ")}`,
            )
            .join("; ")
        : null;

      this.$store.commit("showSnackbar", {
        message: data?.message || changeset || fallback,
        color: "error",
      });
    },
  },
};
</script>

<style scoped>
.rv-page {
  --rv-line: #e4e7ec;
  --rv-ink: #101828;
  --rv-ink-2: #475467;
  --rv-ink-3: #98a2b3;
  --rv-accent: var(--v-primary-base, #00aff1);
  padding: 16px 20px 40px;
}

.rv-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.rv-header__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--rv-ink);
  letter-spacing: -0.2px;
}

.rv-header__sub {
  margin: 4px 0 0;
  max-width: 620px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--rv-ink-2);
}

.rv-scope {
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 12px;
  background: #fff;
}

.rv-scope__field {
  max-width: 200px;
  flex: 0 1 200px;
}

.rv-scope__field--wide {
  max-width: 240px;
  flex: 0 1 240px;
}

.rv-scope__chip {
  font-weight: 600;
}

.rv-scope__dirty {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #b54708;
}

.rv-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 44px 20px;
}

.rv-state--card {
  margin-top: 12px;
  border-radius: 10px;
  background: #fff;
}

.rv-state__title {
  margin: 14px 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: var(--rv-ink);
}

.rv-state__text {
  margin: 0;
  max-width: 560px;
  font-size: 13px;
  line-height: 1.55;
  color: var(--rv-ink-2);
}

.rv-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 12px;
  margin-top: 14px;
}

.rv-card {
  position: relative;
  padding: 14px 16px 10px;
  border-radius: 10px;
  background: #fff;
  border-left: 3px solid var(--rv-accent) !important;
}

.rv-card--unset {
  border-left-color: #f79009 !important;
}

.rv-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.rv-card__name {
  margin: 0;
  font-size: 13.5px;
  font-weight: 700;
  color: var(--rv-ink);
  line-height: 1.35;
}

.rv-card__hsn {
  flex-shrink: 0;
  font-size: 10px !important;
  border-color: var(--rv-line);
  color: var(--rv-ink-3);
}

.rv-card__charged {
  margin: 4px 0 0;
  font-size: 11.5px;
  color: var(--rv-ink-3);
}

.rv-card__inputs {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.rv-card__input {
  flex: 1 1 0;
}

.rv-card__input ::v-deep input {
  text-align: right;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.rv-card__input ::v-deep input::-webkit-outer-spin-button,
.rv-card__input ::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.rv-card__toggle {
  margin-top: 8px;
}

.rv-card__toggle ::v-deep .v-btn {
  text-transform: none;
  letter-spacing: 0;
  font-size: 11px;
  height: 26px !important;
}

.rv-card__nogst {
  margin: 10px 0 0;
  font-size: 11.5px;
  color: var(--rv-ink-3);
}

.rv-card__effect {
  margin: 8px 0 0;
  font-size: 11.5px;
  line-height: 1.45;
  color: var(--rv-ink-2);
}

.rv-card__rate {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-top: 10px;
}

.rv-card__value {
  font-size: 26px;
  font-weight: 700;
  color: var(--rv-ink);
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.rv-card__unit {
  font-size: 15px;
  font-weight: 600;
  color: var(--rv-ink-2);
}

.rv-card__gst {
  font-size: 12px;
  color: var(--rv-ink-2);
}

.rv-card__unsetnote {
  margin: 6px 0 0;
  font-size: 11px;
  font-weight: 600;
  color: #b54708;
}

.rv-card__feenote {
  margin: 6px 0 0;
  font-size: 11px;
  color: var(--rv-ink-3);
}

.rv-card__apply {
  margin: 4px 0 0 -6px;
}

.rv-table-card {
  margin-top: 14px;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
}

.rv-table-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border-bottom: 1px solid var(--rv-line);
}

.rv-table-card__title {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--rv-ink);
}

.rv-table-card__sub {
  margin: 3px 0 0;
  max-width: 520px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--rv-ink-2);
}

.rv-table-card__tools {
  display: flex;
  gap: 8px;
}

.rv-tool {
  max-width: 190px;
  flex: 0 1 190px;
}

.rv-panel {
  background: #fff;
}

.rv-panel:not(:last-child) {
  border-bottom: 1px solid #f0f1f3;
}

.rv-panel__header {
  min-height: 42px !important;
  padding: 6px 16px;
}

.rv-panel__title {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.rv-panel__name {
  font-size: 13px;
  font-weight: 600;
  color: var(--rv-ink);
}

.rv-panel__meta {
  font-size: 11px;
  font-weight: 600;
  color: var(--rv-ink-3);
  font-variant-numeric: tabular-nums;
}

.rv-panel__content {
  background: #fbfcfd;
}

.rv-panel__content ::v-deep .v-expansion-panel-content__wrap {
  padding: 0 16px 14px;
}

.rv-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid var(--rv-line);
  border-radius: 8px;
  overflow: hidden;
}

.rv-th {
  padding: 7px 12px;
  text-align: left;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--rv-ink-2);
  background: #f9fafb;
  border-bottom: 1px solid var(--rv-line);
  white-space: nowrap;
}

.rv-th--num {
  text-align: right;
  width: 130px;
}

.rv-td {
  padding: 5px 12px;
  font-size: 12.5px;
  color: var(--rv-ink-2);
  border-bottom: 1px solid #f4f5f7;
  vertical-align: middle;
}

.rv-td--num > * {
  margin-left: auto;
}

.rv-band {
  padding: 6px 12px;
  background: #f4f6f8;
  border-top: 1px solid var(--rv-line);
  border-bottom: 1px solid var(--rv-line);
}

.rv-band__name {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--rv-ink);
}

.rv-band__copy {
  margin-left: 6px;
}

.rv-fee {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rv-fee__code {
  display: inline-block;
  min-width: 42px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #eef2f6;
  color: #344054;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
}

.rv-fee__inherited {
  padding: 0 5px;
  border-radius: 4px;
  background: #eef2f6;
  color: var(--rv-ink-3);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
}

.rv-fee__name {
  color: var(--rv-ink);
  font-weight: 500;
}

.rv-muted {
  color: var(--rv-ink-3);
}

.rv-input {
  width: 104px;
}

.rv-input ::v-deep input {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.rv-input ::v-deep .v-input__slot {
  min-height: 30px !important;
  padding: 0 8px !important;
}

.rv-input ::v-deep input::-webkit-outer-spin-button,
.rv-input ::v-deep input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.rv-toggle ::v-deep .v-btn {
  text-transform: none;
  letter-spacing: 0;
  font-size: 11px;
  height: 26px !important;
}

.rv-bulk__hint {
  margin: 0 0 12px;
  font-size: 12.5px;
  color: var(--rv-ink-2);
}

@media (max-width: 600px) {
  .rv-page {
    padding: 12px 10px 40px;
  }

  .rv-scope {
    position: static;
  }

  .rv-scope__field,
  .rv-scope__field--wide,
  .rv-tool {
    max-width: none;
    flex: 1 1 100%;
  }
}
</style>
